
import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Phone, Mail, AlertCircle, Check } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { useUI } from "@/context/UIContext";

export default function Auth() {
  const { signIn, signUp, user, loading } = useAuth();
  const { isOnboardingComplete } = useUI();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [authMethod, setAuthMethod] = useState<"email" | "phone">("email");
  const [phone, setPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode');
  const email = searchParams.get('email');
  
  useEffect(() => {
    if (email && mode === 'signup') {
      setLoginEmail(email);
    }
    
    if (mode === 'verification_sent') {
      setIsVerificationSent(true);
    }
  }, [email, mode]);

  useEffect(() => {
    if (user) {
      // Check if user is new by looking at onboarding status
      if (!isOnboardingComplete) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate, isOnboardingComplete]);
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (authMethod === "email") {
      await signIn(loginEmail, loginPassword);
    } else {
      // Handle phone verification code submission
      try {
        const { error } = await supabase.auth.verifyOtp({
          phone,
          token: verificationCode,
          type: "sms"
        });
        
        if (error) throw error;
      } catch (error: any) {
        setError(error.message);
      }
    }
  };
  
  const handlePhoneLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone,
      });
      
      if (error) throw error;
      
      setShowVerification(true);
    } catch (error: any) {
      setError(error.message);
    }
  };
  
  const handleGoogleSignIn = async () => {
    setError(null);
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
    } catch (error: any) {
      setError(error.message);
    }
  };
  
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      await signUp(loginEmail, loginPassword, name);
      
      // After successful signup, retrieve and save the onboarding data
      const onboardingData = localStorage.getItem('onboardingData');
      if (onboardingData) {
        const formData = JSON.parse(onboardingData);
        
        // Save user profile data
        const { error: profileError } = await supabase
          .from('user_profiles')
          .upsert({
            user_id: user?.id,
            ...formData
          });

        if (profileError) throw profileError;
        
        // Save additional notes if they exist
        if (formData.additionalNotes) {
          const { error: notesError } = await supabase
            .from('user_notes')
            .insert({
              user_id: user?.id,
              content: formData.additionalNotes,
              type: 'onboarding_notes'
            });

          if (notesError) {
            console.error("Error saving notes:", notesError);
          }
        }
        
        // Clear the temporary storage
        localStorage.removeItem('onboardingData');
      }
      
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message);
    }
  };
  
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth/setup-password`,
      });
      
      if (error) throw error;
      
      toast({
        title: "Password reset email sent",
        description: "Check your email for a link to reset your password.",
      });
      
      setIsResetMode(false);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setResetLoading(false);
    }
  };
  
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4 flex items-center justify-center">
          <div className="animate-pulse">Loading...</div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full">
          {isVerificationSent ? (
            <Card className="w-full">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 bg-primary/10 rounded-full p-3 w-16 h-16 flex items-center justify-center">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-display font-bold tracking-tight">
                  Check Your Email
                </CardTitle>
                <CardDescription>
                  We've sent you a verification email with instructions to complete your registration.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  Please check your inbox and click the verification link to set your password and complete your account setup.
                </p>
                <div className="bg-muted p-3 rounded-md text-xs text-muted-foreground">
                  <p>If you don't see the email in your inbox:</p>
                  <ul className="list-disc pl-4 mt-2 space-y-1">
                    <li>Check your spam folder</li>
                    <li>Make sure you entered the correct email address</li>
                    <li>Allow a few minutes for the email to arrive</li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => navigate('/auth')}
                >
                  Return to Sign In
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <Card className="w-full">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-display font-bold tracking-tight">
                  {isResetMode ? "Reset Your Password" : "Welcome Back"}
                </CardTitle>
                <CardDescription>
                  {isResetMode 
                    ? "Enter your email to receive a password reset link" 
                    : "Sign in to access your wellness dashboard"}
                </CardDescription>
              </CardHeader>
              
              {error && (
                <div className="px-6 pb-4">
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </div>
              )}
              
              {isResetMode ? (
                <form onSubmit={handlePasswordReset}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email</Label>
                      <Input 
                        id="reset-email" 
                        type="email" 
                        placeholder="your@email.com" 
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button type="submit" className="w-full" disabled={resetLoading}>
                        {resetLoading ? "Sending..." : "Send Reset Link"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsResetMode(false)}
                      >
                        Back to Sign In
                      </Button>
                    </div>
                  </CardContent>
                </form>
              ) : (
                !showVerification ? (
                  <>
                    <div className="px-6 mb-4">
                      <div className="flex border rounded-md overflow-hidden">
                        <Button
                          type="button"
                          variant={authMethod === "email" ? "default" : "outline"}
                          className={`flex-1 rounded-none ${authMethod === "email" ? "" : "border-none"}`}
                          onClick={() => setAuthMethod("email")}
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Email
                        </Button>
                        <Button
                          type="button"
                          variant={authMethod === "phone" ? "default" : "outline"}
                          className={`flex-1 rounded-none ${authMethod === "phone" ? "" : "border-none"}`}
                          onClick={() => setAuthMethod("phone")}
                        >
                          <Phone className="mr-2 h-4 w-4" />
                          Phone
                        </Button>
                      </div>
                    </div>
                    
                    {authMethod === "email" ? (
                      <form onSubmit={handleLogin}>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input 
                              id="email" 
                              type="email" 
                              placeholder="your@email.com" 
                              value={loginEmail}
                              onChange={(e) => setLoginEmail(e.target.value)}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label htmlFor="password">Password</Label>
                              <Button 
                                variant="link" 
                                className="p-0 h-auto text-xs"
                                onClick={() => setIsResetMode(true)}
                                type="button"
                              >
                                Forgot password?
                              </Button>
                            </div>
                            <Input 
                              id="password" 
                              type="password" 
                              placeholder="••••••••" 
                              value={loginPassword}
                              onChange={(e) => setLoginPassword(e.target.value)}
                              required
                            />
                          </div>
                          <div className="flex justify-end">
                            <Button type="submit" className="w-full" disabled={loading}>
                              {loading ? "Signing in..." : "Sign In"}
                            </Button>
                          </div>
                        </CardContent>
                      </form>
                    ) : (
                      <form onSubmit={handlePhoneLogin}>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number</Label>
                            <Input 
                              id="phone" 
                              type="tel" 
                              placeholder="+1XXXXXXXXXX" 
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              required
                            />
                            <p className="text-xs text-muted-foreground">
                              Please enter your phone number in international format
                            </p>
                          </div>
                          <div className="flex justify-end">
                            <Button className="w-full" type="submit" disabled={loading}>
                              {loading ? "Sending code..." : "Send Verification Code"}
                            </Button>
                          </div>
                        </CardContent>
                      </form>
                    )}
                  </>
                ) : (
                  <form onSubmit={handleLogin}>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="verification-code">Verification Code</Label>
                        <Input 
                          id="verification-code" 
                          type="text" 
                          placeholder="Enter code" 
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          required
                        />
                        <p className="text-xs text-muted-foreground">
                          Enter the verification code sent to {phone}
                        </p>
                      </div>
                      <div className="flex justify-end">
                        <Button className="w-full" type="submit" disabled={loading}>
                          {loading ? "Verifying..." : "Verify and Sign In"}
                        </Button>
                        <Button 
                          className="w-full" 
                          type="button" 
                          variant="outline"
                          onClick={() => setShowVerification(false)}
                        >
                          Back
                        </Button>
                      </div>
                    </CardContent>
                  </form>
                )
              )}
              
              {!isResetMode && !showVerification && !isVerificationSent && (
                <div className="px-6 pb-6">
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-card px-2 text-xs text-muted-foreground">
                        OR CONTINUE WITH
                      </span>
                    </div>
                  </div>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={handleGoogleSignIn}
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2" aria-hidden="true">
                      <path
                        d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                        fill="#EA4335"
                      />
                      <path
                        d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                        fill="#4285F4"
                      />
                      <path
                        d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                        fill="#34A853"
                      />
                    </svg>
                    Google
                  </Button>
                </div>
              )}
              
              <div className="px-6 pb-4 text-center">
                <p className="text-sm text-muted-foreground">
                  New to Vitatee?{" "}
                  <Link to="/pricing" className="text-primary hover:underline">
                    View Plans
                  </Link>
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
